import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useT } from "../lib/i18n";
import { Sparkle, X, ArrowRight } from "@phosphor-icons/react";

const STORAGE_KEY = "zcardon_smart_banner_dismissed";

/**
 * Smart app banner — "Open this in the Zcardon app".
 *
 * Distinct from `IosInstallPrompt`: that one teaches **install**, this one
 * teaches **open in the already-installed app**. We have no reliable way to
 * detect installation from the browser, so we offer a one-tap deep-link:
 *
 *   zcardon://<path><search>
 *
 * iOS opens the app if it's installed, or silently no-ops if not — both
 * outcomes are fine. We never force a redirect to the App Store from JS
 * (jarring UX) — once `apple-itunes-app` is wired with a real App-Store ID
 * Apple's native banner takes over and handles the install fallback for us.
 *
 * Banner only renders on **receiver-facing public routes** where the deep
 * link is high-value (`/pay`, `/cash-request/*`, `/track*`). Customers
 * already inside the dashboard don't need it.
 */
function isIos() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iosUa = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = /Mac/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
    return iosUa || iPadOS;
}
function isStandalone() {
    if (typeof window === "undefined") return false;
    return Boolean(window.navigator.standalone) || window.matchMedia("(display-mode: standalone)").matches;
}
function isWebview() {
    if (typeof navigator === "undefined") return false;
    return /(FBAN|FBAV|Instagram|Line|Twitter|TikTok|MicroMessenger)/i.test(navigator.userAgent || "");
}

const RECEIVER_ROUTES = [/^\/pay\b/, /^\/cash-request\b/, /^\/track\b/];

export default function SmartAppBanner() {
    const t = useT();
    const loc = useLocation();
    const [show, setShow] = useState(false);

    useEffect(() => {
        const onReceiverRoute = RECEIVER_ROUTES.some((re) => re.test(loc.pathname));
        if (!onReceiverRoute) { setShow(false); return; }
        try {
            if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
        } catch { return; }
        if (!isIos() || isStandalone() || isWebview()) return;
        const t0 = setTimeout(() => setShow(true), 800);
        return () => clearTimeout(t0);
    }, [loc.pathname]);

    const dismiss = () => {
        try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
        setShow(false);
    };

    const openInApp = () => {
        // Forward the current page (path + query) to the native app via the
        // custom URL scheme. The Zcardon app's iOS scaffold registers the
        // `zcardon://` scheme in Info.plist; the React DeepLinkHandler then
        // routes the inbound URL inside the app.
        const target = `zcardon://${loc.pathname.replace(/^\//, "")}${loc.search || ""}${loc.hash || ""}`;
        window.location.href = target;
    };

    if (!show) return null;

    return (
        <div
            className="fixed top-0 inset-x-0 z-[60] bg-[#112318] text-[#FBF9F6] shadow-lg animate-in slide-in-from-top-4"
            data-testid="smart-app-banner"
        >
            <div className="max-w-2xl mx-auto px-4 py-2.5 flex items-center gap-3">
                <button
                    type="button"
                    onClick={dismiss}
                    className="shrink-0 p-1 rounded-full hover:bg-white/10 transition-colors"
                    aria-label={t("smart_banner.dismiss")}
                    data-testid="smart-app-banner-dismiss"
                >
                    <X size={16} weight="bold" />
                </button>
                <div className="shrink-0 w-9 h-9 rounded-lg bg-[#184A30] flex items-center justify-center">
                    <Sparkle size={18} weight="duotone" className="text-[#FBF9F6]" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight">{t("smart_banner.title")}</p>
                    <p className="text-[11px] text-white/60 leading-tight mt-0.5 truncate">
                        {t("smart_banner.subtitle")}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openInApp}
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#FBF9F6] text-[#112318] text-xs font-semibold hover:bg-white transition-colors"
                    data-testid="smart-app-banner-open"
                >
                    {t("smart_banner.cta")} <ArrowRight size={12} weight="bold" />
                </button>
            </div>
        </div>
    );
}
